import React, { createContext, useContext, useState, ReactNode } from 'react';
import {CartItem} from "../../interfaces/interfaces";


interface CartContextType {
    cart: CartItem[];
    addToCart: (item: Omit<CartItem, 'qty'>) => void;
    removeFromCart: (id: number) => void;
    updateQty: (id: number, qty: number) => void;
    clearCart: () => void;
    totalItems: number;
}

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({ children }: { children: ReactNode }) => {
    const [cart, setCart] = useState<CartItem[]>([]);

    const addToCart = (item: Omit<CartItem, 'qty'>) => {
        setCart(prev => {
            const existing = prev.find(p => p.id === item.id);
            if (existing) return prev.map(p => p.id === item.id ? { ...p, qty: p.qty + 1 } : p);
            return [...prev, { ...item, qty: 1 }];
        });
    };

    const removeFromCart = (id: number) => setCart(prev => prev.filter(p => p.id !== id));

    const updateQty = (id: number, qty: number) => {
        if (qty <= 0) return removeFromCart(id);
        setCart(prev => prev.map(p => p.id === id ? { ...p, qty } : p));
    };

    const clearCart = () => setCart([]);

    const totalItems = cart.reduce((sum, p) => sum + p.qty, 0);

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQty, clearCart, totalItems }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used inside CartProvider');
    return ctx;
};
